// Cache local hors connexion du chauffeur, limité aux demandes de documents à récupérer (+ statut des modules).
// Sécurité : contenu chiffré AES-GCM 256 (WebCrypto) avec une clé NON exportable conservée dans IndexedDB, propre à l'utilisateur ;
// clé et données détruites à la déconnexion / expiration de session / changement d'utilisateur ; entrées expirées après 72 h.
// Sans WebCrypto (contexte non sécurisé), rien n'est écrit sur l'appareil : cache en mémoire uniquement.
const PREFIX = "sanitdocs:cache:v3:";
const SYNC_PREFIX = "sanitdocs:lastSync:v3:";
const LEGACY = [/^sanitdocs:cache:v1$/, /^sanitdocs:lastSync$/, /^sanitdocs:cache:v2:/, /^sanitdocs:lastSync:v2:/];
const IDB = "sanitdocs-offline-keys";
const MAX_AGE = 72 * 3600 * 1000;
const CACHEABLE = [/^\/requests\/grouped/, /^\/requests\/mine/, /^\/requests\/by-establishment\//, /^\/appointments\/module-status/];
let offline = typeof navigator !== "undefined" && navigator.onLine === false;
let uid = null;
let mem = {};
let ready = Promise.resolve();
let key = null;
let persistTimer = null;
const listeners = new Set();
const subtle = typeof window !== "undefined" && window.isSecureContext && window.crypto?.subtle;

const idb = (fn) => new Promise((res, rej) => {
  if (!window.indexedDB) return rej(new Error("no idb"));
  const o = indexedDB.open(IDB, 1);
  o.onupgradeneeded = () => o.result.createObjectStore("keys");
  o.onerror = () => rej(o.error);
  o.onsuccess = () => { const tx = o.result.transaction("keys", "readwrite"); const r = fn(tx.objectStore("keys")); tx.oncomplete = () => { res(r?.result); o.result.close(); }; tx.onerror = () => rej(tx.error); };
});
async function userKey(id, create) {
  let k = await idb((s) => s.get(id)).catch(() => null);
  if (!k && create) { k = await subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]); await idb((s) => s.put(k, id)); }
  return k || null;
}
const b64 = (buf) => { const a = new Uint8Array(buf); let s = ""; for (let i = 0; i < a.length; i += 0x8000) s += String.fromCharCode.apply(null, a.subarray(i, i + 0x8000)); return btoa(s); };
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const fresh = (c) => Object.fromEntries(Object.entries(c).filter(([, v]) => Date.now() - v.at < MAX_AGE));

async function loadEncrypted(id) {
  if (!subtle) return {};
  key = await userKey(id, true).catch(() => null);
  const raw = localStorage.getItem(PREFIX + id);
  if (!key || !raw) return {};
  try { const { iv, ct } = JSON.parse(raw); return fresh(JSON.parse(new TextDecoder().decode(await subtle.decrypt({ name: "AES-GCM", iv: unb64(iv) }, key, unb64(ct))))); }
  catch { localStorage.removeItem(PREFIX + id); return {}; }
}
function persist() {
  clearTimeout(persistTimer);
  persistTimer = setTimeout(async () => {
    if (!subtle || !key || !uid) return;
    const id = uid; const iv = crypto.getRandomValues(new Uint8Array(12));
    try { const ct = await subtle.encrypt({ name: "AES-GCM", iv }, key, new TextEncoder().encode(JSON.stringify(fresh(mem)))); if (uid === id) localStorage.setItem(PREFIX + id, JSON.stringify({ iv: b64(iv), ct: b64(ct) })); } catch (_) {}
  }, 300);
}
const cacheKey = (config) => `${config.url}${config.params ? "?" + new URLSearchParams(config.params).toString() : ""}`;
const allKeys = () => { const out = []; for (let i = 0; i < localStorage.length; i++) out.push(localStorage.key(i)); return out; };
const purgeStorage = (keep) => { try { allKeys().forEach((k) => { if (LEGACY.some((r) => r.test(k)) || ((k.startsWith(PREFIX) || k.startsWith(SYNC_PREFIX)) && (!keep || !k.endsWith(":" + keep)))) localStorage.removeItem(k); }); } catch (_) {} };

// Ne conserve que les données de l'utilisateur connecté ; toute donnée (et clé) d'un autre compte présente sur l'appareil est effacée.
export const setCacheUser = (id) => {
  if (!id || id === uid) return ready;
  uid = id; mem = {}; key = null;
  purgeStorage(id);
  ready = idb((s) => s.getAllKeys()).then((ks) => Promise.all((ks || []).filter((k) => k !== id).map((k) => idb((s) => s.delete(k))))).catch(() => {})
    .then(() => loadEncrypted(id)).then((c) => { if (uid === id) mem = { ...c, ...mem }; }).catch(() => {});
  return ready;
};
export const clearOfflineData = () => {
  uid = null; mem = {}; key = null; clearTimeout(persistTimer);
  purgeStorage(null);
  try { window.indexedDB?.deleteDatabase(IDB); } catch (_) {}
};

export const isCacheable = (config) => !!uid && (config.method || "get").toLowerCase() === "get" && CACHEABLE.some((r) => r.test(config.url || ""));
export const putCache = (config, data) => { mem[cacheKey(config)] = { data, at: Date.now() }; persist(); try { localStorage.setItem(SYNC_PREFIX + uid, String(Date.now())); } catch (_) {} setOffline(false); };
export const getCache = async (config) => { await ready; const hit = mem[cacheKey(config)]; return hit && Date.now() - hit.at < MAX_AGE ? hit : null; };
export const getLastSync = () => (uid ? Number(localStorage.getItem(SYNC_PREFIX + uid)) || null : null);
export const isOffline = () => offline;
export const setOffline = (v) => { if (offline !== v) { offline = v; listeners.forEach((l) => l(v)); } };
export const onNetworkChange = (fn) => {
  listeners.add(fn);
  const on = () => { setOffline(false); window.dispatchEvent(new CustomEvent("sanitdocs:resync")); };
  const off = () => setOffline(true);
  window.addEventListener("online", on); window.addEventListener("offline", off);
  return () => { listeners.delete(fn); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
};
export const isNetworkError = (err) => !err.response && (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED" || err.message === "Network Error");

// Démarrage : session restaurée avant le premier rendu, pour que les écrans servent le cache dès la première requête.
try { const u = JSON.parse(localStorage.getItem("sanitdocs_user") || "null"); if (u?.id) setCacheUser(u.id); } catch (_) {}
